import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import { isBetweenUnbounded } from 'modules/utils/DateUtils';
import { objectToQsParam } from 'modules/utils/UrlUtils';
import { createSelector } from 'reselect';
import { fhirCurrentUserPatientId } from 'store/authentication/selectors';
import { eventListDataSelector, hiddenEventListDataSelector } from 'store/events/selectors';
import { GlobalSearchItem } from 'store/global/types';
import { activeEncounterMedicationsLoadingSelector } from 'store/medications/selectors';
import { RootState } from 'store/types';
import { OptionType, VisitSummary, VisitSummaryFilter } from 'store/visitSummary/types';
import { activeVisitVitalsLoadingSelector } from '../vitals/selectors';

const dataSelector = (state: { data: object }) => state.data;

export const visitSummaryListSelector = (state: RootState) => state.visitSummary.visits;

export const visitSummaryListIsSelecting = createSelector(
  visitSummaryListSelector,
  visits => visits.isSelecting
);

export const visitSummaryListSelected = (id: number) =>
  createSelector(visitSummaryListSelector, visits => visits.selected[id]);

export const visitSummaryListAllSelected = createSelector(
  visitSummaryListSelector,
  visits => visits.selected
);

export const visitSummaryListLoadingSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.isFetching
);

export const visitSummaryFullListLoadingSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.isFetchingFullList
);

export const visitSummaryShortListErrorSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.errorShortList
);

export const visitSummaryFullListErrorSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.errorFullList
);

export const visitSummaryListTotalCountSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.totalVisits
);

export const visitSummaryShortListRequestStateSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.shortListRequestState
);

export const visitSummaryFullListRequestStateSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.fullListRequestState
);

export const visitSummaryListErrorSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.error
);

export const visitSummaryListDataSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.data
);

export const completedEVisitListSelector = createSelector(
  [visitSummaryListSelector, eventListDataSelector, hiddenEventListDataSelector],
  (visits, events, hiddenEvents) => {
    const currentDate = dayjs().startOf('day');
    const completedEVisits: VisitSummary[] = [];

    visits.data.forEach(visit => {
      const visitDate = dayjs(visit.date).startOf('day');
      const dayDifference = currentDate.diff(visitDate, 'day');
      const visitEvent = [...events, ...hiddenEvents].find(event => event.sourceId === visit.id);

      if (
        visit.locationObject.locationName === 'CCCL_EVisit' &&
        visit.status === 'finished' &&
        dayDifference <= 7 &&
        !visitEvent?.hidden &&
        !visitEvent?.read
      ) {
        completedEVisits.push({ ...visit, eventId: visitEvent ? visitEvent.id : undefined });
      }
    });
    return completedEVisits;
  }
);

export const pendingEVisitListSelector = createSelector(
  [visitSummaryListSelector, eventListDataSelector],
  visits => {
    const pendingEVisits: VisitSummary[] = visits.data.filter(visit => {
      return visit.locationObject.locationName === 'CCCL_EVisit' && visit.status === 'in-progress';
    });
    return pendingEVisits;
  }
);

export const visitSummaryFilterSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.filter
);

export const visitSummarySortBySelector = createSelector(
  visitSummaryListSelector,
  visits => visits.sortBy
);

export const visitSummaryDetailsSelector = (state: RootState) =>
  state.visitSummary.activeVisitDetails;

export const visitSummaryDetailsLoadingSelector = createSelector(
  visitSummaryDetailsSelector,
  activeVisitDetails => activeVisitDetails.isFetching
);

export const visitSummaryDetailsErrorSelector = createSelector(
  visitSummaryDetailsSelector,
  activeVisitDetails => activeVisitDetails.error
);

export const visitSummaryDetailsDataSelector = createSelector(
  visitSummaryDetailsSelector,
  activeVisitDetails => activeVisitDetails.data
);

export const visitSummaryDocumentSelector = (state: RootState) =>
  state.visitSummary.activeVisitDocument;

export const visitSummaryDocumentLoadingSelector = createSelector(
  visitSummaryDocumentSelector,
  activeVisitDocument => activeVisitDocument.isFetching
);

export const visitSummaryDocumentErrorSelector = createSelector(
  visitSummaryDocumentSelector,
  activeVisitDocument => activeVisitDocument.error
);

export const visitSummaryDocumentDataSelector = createSelector(
  visitSummaryDocumentSelector,
  activeVisitDocument => activeVisitDocument.data
);

export const visitSummaryDataSelector = createSelector(visitSummaryListSelector, visitSummaryList =>
  orderBy(dataSelector(visitSummaryList), ['date'], ['desc'])
);

export const visitSummaryFilteredAndSortedDataSelector = createSelector(
  [visitSummaryListDataSelector, visitSummaryFilterSelector, visitSummarySortBySelector],
  (data, filter, sortBy) => {
    let result = [...data];

    result = orderBy(result, ['date'], [(sortBy as OptionType)?.value || 'desc']);

    if (!isEmpty(filter)) {
      const {
        provider,
        location,
        visitType,
        startDate,
        endDate,
        year
      } = filter as VisitSummaryFilter;

      if (provider) {
        result = result.filter(r => r.primaryProvider?.providerId === provider);
      }

      if (location) {
        result = result.filter(r => r.locationObject.locationId === location);
      }

      if (visitType) {
        result = result.filter(r => r.visitType === visitType);
      }

      if (year) {
        result = result.filter(r => {
          return +year === dayjs(r.date).year();
        });
      } else if (startDate || endDate) {
        result = result.filter(r => {
          const date = dayjs(r.date).startOf('day');
          return isBetweenUnbounded(date, startDate, endDate);
        });
      }
    }

    return result;
  }
);

export const visitSummaryFiltersActiveSelector = createSelector(
  [visitSummaryFilterSelector],
  filter => filter && Object.values(filter).some(f => !!f)
);

export const visitSummaryHasVisitsSelector = createSelector(
  visitSummaryListDataSelector,
  data => data && !!data.length
);

export const visitSummaryScreenLoadingSelector = createSelector(
  [
    visitSummaryDetailsLoadingSelector,
    activeVisitVitalsLoadingSelector,
    activeEncounterMedicationsLoadingSelector
  ],
  (loadingVisitDetails, loadingVitals, loadingMedications) => {
    return loadingVisitDetails || loadingVitals || loadingMedications;
  }
);

export const visitSummaryDownloadsSelector = (state: RootState) => state.visitSummary.downloads;

export const visitSummaryMultipleDownloadsSelector = (state: RootState) =>
  state.visitSummary.multipleDownloads;

export const multipleDownloadsByCernerPersonId = (state: RootState) => {
  const cernerPersonId = fhirCurrentUserPatientId(state);
  const multipleDownloads = visitSummaryMultipleDownloadsSelector(state);
  return multipleDownloads?.[cernerPersonId];
};

/**
 * Share Visit Summary CCD
 */
export const shareVisitSummaryCcdSelector = (state: RootState) => state.visitSummary.shareCcd;

export const visitSummaryShareCcdDataSelector = createSelector(
  shareVisitSummaryCcdSelector,
  shareCcd => shareCcd.data
);

export const visitSummaryShareCcdSendingSelector = createSelector(
  shareVisitSummaryCcdSelector,
  shareCcd => shareCcd.isSending
);

export const visitSummaryListLastUpdatedSelector = createSelector(
  visitSummaryListSelector,
  visits => visits.cacheUpdatedAt
);

/**
 * Share Multiple Visit Summary CCD
 */

export const shareMultipleVisitSummaryCcdSelector = (state: RootState) =>
  state.visitSummary.multipleShareCcd;

export const visitSummaryMultipleShareCcdDataSelector = createSelector(
  shareMultipleVisitSummaryCcdSelector,
  multipleShareCcd => multipleShareCcd.data
);

export const visitSummaryMultipleShareCcdSendingSelector = createSelector(
  shareMultipleVisitSummaryCcdSelector,
  multipleShareCcd => multipleShareCcd.isSending
);

export const getVisitSummaryLinkPath = (visit: VisitSummary) => {
  const urlBase = `/u/health-record/visit-summaries/live`;
  const urlSegment1 = `${visit.source}/${visit.id}`;
  let url = `${urlBase}/${urlSegment1}/${visit.date}`;
  if (!isEmpty(visit.mhpDetails)) {
    url = `${urlBase}-detailed/${urlSegment1}/${objectToQsParam(visit.mhpDetails)}`;
  }
  return url;
};

export const searchFormattedVisitSummaries = createSelector(visitSummaryListDataSelector, data => {
  return data.map(
    (item): GlobalSearchItem => {
      const params = {
        source: item?.source,
        id: item?.id,
        dateOfEncounter: item?.date,
        mhpDetails: item?.mhpDetails
      };
      return {
        title: item?.visitDescription || '',
        provider: item?.primaryProvider?.providerName,
        location: item?.locationObject?.locationName,
        date: item?.date,
        keywords: [item?.chiefComplaint?.valueString || '', item?.visitType],
        icon: 'VisitSummaryIcon',
        category: 'VisitSummaries',
        navWebPath: getVisitSummaryLinkPath(item),
        navParams: params
      };
    }
  );
});
